import React from "react";
import { Document, Page, Text, View, StyleSheet, Image } from "@react-pdf/renderer";

// =======================
// STYLES
// =======================
const styles = StyleSheet.create({
    page: { fontSize: 10, fontFamily: "Helvetica", flexDirection: "column", padding: 0 },
    table: { display: "table", width: "auto", marginTop: 5 },
    tableRow: { flexDirection: "row" },
    firstCol: { borderLeftWidth: 1 },
    topBorder: { borderTopWidth: 1 },
    tableCol: {
        borderStyle: "solid",
        borderWidth: 1,
        borderColor: "#000",
        borderTopWidth: 0,
        borderLeftWidth: 0,
        padding: 4,
        flexWrap: "wrap",
        wordBreak: "break-word",
    },
    tableColHeader: { backgroundColor: "#e0e0e0", fontWeight: "bold", textAlign: "center" },
    nodeRow: { backgroundColor: "#cce5ff", fontWeight: "bold", padding: 4 },
    groupRow: { backgroundColor: "#e6f2ff", fontWeight: "bold", padding: 4 },
    subtotalRow: { backgroundColor: "#f0f0f0", fontWeight: "bold", padding: 4 },
    totalRow: { backgroundColor: "#b3b3b3", fontWeight: "bold", padding: 4 },
    textLeft: { textAlign: "left" },
    textRight: { textAlign: "right" },
    headerImage: { padding: 2 },
});

// =======================
// HEADER COMPONENT
// =======================
const apiPaths_from_local_storage = localStorage.getItem("apiPaths")
    ? JSON.parse(localStorage.getItem("apiPaths"))
    : "-";

const Header = ({ clientDetails }) => (
    <View style={{ width: "100%" }} fixed>
        <View style={{ padding: 10 }}>
            <View style={{ width: "100%", flexDirection: "column" }}>
                {apiPaths_from_local_storage[0]?.REP_HEADER?.includes("data:image/") && (
                    <Image src={apiPaths_from_local_storage[0].REP_HEADER} style={styles.headerImage} />
                )}
                <View>
                    <View style={{ textAlign: "center", marginTop: 10 }}>
                        <Text style={{ fontSize: 12, fontWeight: "bold", marginTop: 5 }}>
                            Balance Sheet Detailed Report
                        </Text>
                    </View>
                </View>
            </View>
        </View>
    </View>
);

// =======================
// FOOTER COMPONENT
// =======================
const Footer = ({ clientDetails }) => (
    <View style={{ width: "100%" }} fixed>
        <View style={{ width: "100%", padding: 10 }}>
            <View style={{ width: "100%", fontSize: 10 }}>
                <Text
                    style={{ textAlign: "center" }}
                    render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
                />
            </View>
        </View>
    </View>
);

// =======================
// HELPER FUNCTION
// =======================
const formatRow = (row) => {
    const balance = row.BALANCE || 0;
    return {
        ...row,
        DEBIT: balance > 0 ? balance : null,
        CREDIT: balance < 0 ? Math.abs(balance) : null,
    };
};

// =======================
// TABLE CONTENT COMPONENT
// =======================
const TableContent = ({ groupedData, columns, colWidths, grandTotal }) => {
    const difference = grandTotal.DEBIT - grandTotal.CREDIT; // Calculate the difference

    return (
        <View style={{ flexGrow: 1 }} wrap>
            <View style={{ padding: 10 }}>
                {/* Table Header */}
                <View style={styles.tableRow} fixed>
                    {columns.map((col, i) => {
                        const colStyle = col.column_key_from_api === "ACCOUNT" ? { width: 70 } : { flex: colWidths[i] };
                        return (
                            <View
                                key={i}
                                style={[
                                    styles.tableCol,
                                    styles.tableColHeader,
                                    styles.topBorder,
                                    i === 0 && styles.firstCol,
                                    colStyle,
                                ]}
                            >
                                <Text>{col.column_name}</Text>
                            </View>
                        );
                    })}
                </View>

                {/* Table Rows */}
                {Object.entries(groupedData).map(([node, groups], nodeIndex) => {
                    const nodeSubtotal = Object.values(groups)
                        .flat()
                        .reduce(
                            (acc, row) => {
                                const formatted = formatRow(row);
                                return {
                                    DEBIT: acc.DEBIT + (formatted.DEBIT || 0),
                                    CREDIT: acc.CREDIT + (formatted.CREDIT || 0),
                                };
                            },
                            { DEBIT: 0, CREDIT: 0 }
                        );

                    return (
                        <React.Fragment key={nodeIndex}>
                            {/* Node Row */}
                            <View style={styles.tableRow} wrap={false}>
                                <View
                                    style={[
                                        styles.tableCol,
                                        styles.nodeRow,
                                        styles.firstCol,
                                        { flex: colWidths.reduce((a, b) => a + b) },
                                    ]}
                                >
                                    <Text>{node}</Text>
                                </View>
                            </View>

                            {/* Groups */}
                            {Object.entries(groups).map(([groupCode, rows], groupIndex) => {
                                const groupSubtotal = rows.reduce(
                                    (acc, row) => {
                                        const formatted = formatRow(row);
                                        return {
                                            DEBIT: acc.DEBIT + (formatted.DEBIT || 0),
                                            CREDIT: acc.CREDIT + (formatted.CREDIT || 0),
                                        };
                                    },
                                    { DEBIT: 0, CREDIT: 0 }
                                );

                                return (
                                    <React.Fragment key={groupIndex}>
                                        {/* Group Header */}
                                        <View style={styles.tableRow} wrap={false}>
                                            <View
                                                style={[
                                                    styles.tableCol,
                                                    styles.groupRow,
                                                    styles.firstCol,
                                                    { flex: colWidths.reduce((a, b) => a + b) },
                                                ]}
                                            >
                                                <Text>{groupCode}</Text>
                                            </View>
                                        </View>

                                        {/* Rows */}
                                        {rows.map((row, i) => {
                                            const formatted = formatRow(row);
                                            return (
                                                <View style={styles.tableRow} key={i} wrap={false}>
                                                    {columns.map((col, ci) => {
                                                        const colStyle =
                                                            col.column_key_from_api === "ACCOUNT"
                                                                ? { width: 70 }
                                                                : { flex: colWidths[ci] };
                                                        let content = "";
                                                        if (col.column_key_from_api === "DEBIT")
                                                            content = formatted.DEBIT ? formatted.DEBIT.toFixed(2) : "";
                                                        else if (col.column_key_from_api === "CREDIT")
                                                            content = formatted.CREDIT ? formatted.CREDIT.toFixed(2) : "";
                                                        else content = row[col.column_key_from_api];

                                                        return (
                                                            <View key={ci} style={[styles.tableCol, ci === 0 && styles.firstCol, colStyle]}>
                                                                <Text
                                                                    style={
                                                                        ["DEBIT", "CREDIT"].includes(col.column_key_from_api)
                                                                            ? styles.textRight
                                                                            : styles.textLeft
                                                                    }
                                                                >
                                                                    {content}
                                                                </Text>
                                                            </View>
                                                        );
                                                    })}
                                                </View>
                                            );
                                        })}

                                        {/* Group Subtotal */}
                                        <View style={styles.tableRow} wrap={false}>
                                            {columns.map((col, i) => {
                                                let content = "";
                                                if (col.column_key_from_api === "DEBIT") content = groupSubtotal.DEBIT.toFixed(2);
                                                else if (col.column_key_from_api === "CREDIT") content = groupSubtotal.CREDIT.toFixed(2);
                                                else if (i === 0) content = "Subtotal:";

                                                const colStyle =
                                                    col.column_key_from_api === "ACCOUNT" ? { width: 70 } : { flex: colWidths[i] };
                                                return (
                                                    <View key={i} style={[styles.tableCol, styles.subtotalRow, i === 0 && styles.firstCol, colStyle]}>
                                                        <Text
                                                            style={
                                                                ["DEBIT", "CREDIT"].includes(col.column_key_from_api)
                                                                    ? styles.textRight
                                                                    : styles.textLeft
                                                            }
                                                        >
                                                            {content}
                                                        </Text>
                                                    </View>
                                                );
                                            })}
                                        </View>
                                    </React.Fragment>
                                );
                            })}

                            {/* Node Total */}
                            <View style={styles.tableRow} wrap={false}>
                                {columns.map((col, i) => {
                                    let content = "";
                                    if (col.column_key_from_api === "DEBIT") content = nodeSubtotal.DEBIT.toFixed(2);
                                    else if (col.column_key_from_api === "CREDIT") content = nodeSubtotal.CREDIT.toFixed(2);
                                    else if (i === 0) content = "Node Total:";

                                    const colStyle =
                                        col.column_key_from_api === "ACCOUNT" ? { width: 70 } : { flex: colWidths[i] };
                                    return (
                                        <View key={i} style={[styles.tableCol, styles.totalRow, i === 0 && styles.firstCol, colStyle]}>
                                            <Text
                                                style={
                                                    ["DEBIT", "CREDIT"].includes(col.column_key_from_api)
                                                        ? styles.textRight
                                                        : styles.textLeft
                                                }
                                            >
                                                {content}
                                            </Text>
                                        </View>
                                    );
                                })}
                            </View>
                        </React.Fragment>
                    );
                })}

                {/* Grand Total */}
                <View style={styles.tableRow} wrap={false}>
                    {columns.map((col, i) => {
                        let content = "";
                        if (col.column_key_from_api === "DEBIT") content = grandTotal.DEBIT.toFixed(2);
                        else if (col.column_key_from_api === "CREDIT") content = grandTotal.CREDIT.toFixed(2);
                        else if (i === 0) content = "Grand Total:";

                        const colStyle = col.column_key_from_api === "ACCOUNT" ? { width: 70 } : { flex: colWidths[i] };
                        return (
                            <View key={i} style={[styles.tableCol, styles.totalRow, i === 0 && styles.firstCol, colStyle]}>
                                <Text
                                    style={
                                        ["DEBIT", "CREDIT"].includes(col.column_key_from_api)
                                            ? styles.textRight
                                            : styles.textLeft
                                    }
                                >
                                    {content}
                                </Text>
                            </View>
                        );
                    })}
                </View>

                {/* Difference Row (showing value in last column) */}
                <View style={styles.tableRow} wrap={false}>
                    {columns.map((col, i) => {
                        let content = "";
                        if (i === columns.length - 1) {
                            // Show the difference in the last column (usually CREDIT)
                            content = difference.toFixed(2);
                        } else if (i === 0) {
                            content = "Difference:"; // Label for the difference row
                        }

                        const colStyle = col.column_key_from_api === "ACCOUNT" ? { width: 70 } : { flex: colWidths[i] };
                        return (
                            <View key={i} style={[styles.tableCol, styles.totalRow, i === 0 && styles.firstCol, colStyle]}>
                                <Text
                                    style={
                                        ["DEBIT", "CREDIT"].includes(col.column_key_from_api)
                                            ? styles.textRight
                                            : styles.textLeft
                                    }
                                >
                                    {content}
                                </Text>
                            </View>
                        );
                    })}
                </View>
            </View>
        </View>
    );
};



// =======================
// MAIN PDF COMPONENT
// =======================
const BalanceSheetDetailedPDF = ({ data, parentDetails, selected_mode_table_structure, clientDetails }) => {
    const reportData = data || [];

    const selectedMode = parentDetails.mode_list_array.find((m) => m.is_selected);

    // Filter out NODE, GROUP CODE, and BALANCE
    const columns = selected_mode_table_structure
        .find((item) => item.mode_table_code === selectedMode?.mode_code)
        ?.mode_table_columns.filter(
            (col) =>
                col.column_key_from_api !== "NODE" &&
                col.column_key_from_api !== "GROUP CODE" &&
                col.column_key_from_api !== "BALANCE"
        );

    const colWidths = columns.map((col) => (["DEBIT", "CREDIT"].includes(col.column_key_from_api) ? 1 : 2));

    const groupedData = reportData.reduce((acc, row) => {
        if (!acc[row.NODE]) acc[row.NODE] = {};
        if (!acc[row.NODE][row["GROUP CODE"]]) acc[row.NODE][row["GROUP CODE"]] = [];
        acc[row.NODE][row["GROUP CODE"]].push(row);
        return acc;
    }, {});

    const grandTotal = reportData.reduce(
        (acc, r) => {
            const formatted = formatRow(r);
            return {
                DEBIT: acc.DEBIT + (formatted.DEBIT || 0),
                CREDIT: acc.CREDIT + (formatted.CREDIT || 0),
            };
        },
        { DEBIT: 0, CREDIT: 0 }
    );

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                <Header clientDetails={clientDetails} />
                <TableContent
                    groupedData={groupedData}
                    columns={columns}
                    colWidths={colWidths}
                    grandTotal={grandTotal}
                />
                <Footer clientDetails={clientDetails} />
            </Page>
        </Document>
    );
};

export default BalanceSheetDetailedPDF;
